import { useCallback, useEffect, useMemo, useState } from "react";
import { ImagePlus, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { CATALOG_CATEGORIES, categoryMeta } from "@/data/catalog";
import { call, errText, getJson, mediaUrl } from "@/lib/api";
import { ItemForm } from "./ItemForm";
import { AiExpand } from "./AiExpand";

export const upload = async (path, file, fields = {}) => {
  const fd = new FormData();
  fd.append("file", file);
  Object.entries(fields).forEach(([k, v]) => v !== undefined && v !== null && v !== "" && fd.append(k, v));
  const res = await call(path, { method: "POST", body: fd });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(errText(data?.detail));
  return data;
};

const FilePick = ({ onFile, testid, children, className = "" }) => (
  <label className={`cursor-pointer ${className}`} data-testid={testid}>
    {children}
    <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onFile(f); }} />
  </label>
);

const Row = ({ it, onEdit, onChange, onDelete }) => {
  const [busy, setBusy] = useState(false);
  const owned = async (val) => {
    const res = await call(`/admin/catalog/items/${it.id}/owned`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ owned: val }) });
    if (res.ok) onChange(await res.json());
  };
  const photo = async (f) => {
    setBusy(true);
    try { onChange(await upload(`/admin/catalog/items/${it.id}/image`, f)); toast.success("Foto actualizada"); } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  return (
    <tr className="border-t border-white/10 align-middle" data-testid={`admin-item-row-${it.id}`}>
      <td className="px-3 py-2">
        <div className="grid h-12 w-12 place-items-center overflow-hidden rounded-lg bg-white/5">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <img src={mediaUrl(it.image_url) ?? categoryMeta(it.category).img} alt="" className={`h-full w-full object-contain ${it.image_url ? "" : "opacity-40"}`} />}
        </div>
      </td>
      <td className="px-3 py-2"><p>{it.name}</p><p className="text-xs text-[var(--muted)]">{it.version || it.line}</p></td>
      <td className="px-3 py-2 text-[var(--muted)]">{it.manufacturer}<br />{it.year}</td>
      <td className="px-3 py-2 text-[var(--muted)]">{categoryMeta(it.category).label}{it.source === "ia" && <span className="ml-2 rounded-full bg-[rgba(123,199,77,0.15)] px-2 py-0.5 text-[10px] text-[var(--green)]">IA</span>}</td>
      <td className="px-3 py-2"><Switch checked={!!it.owned} onCheckedChange={owned} data-testid={`admin-owned-switch-${it.id}`} /></td>
      <td className="px-3 py-2">
        <div className="flex gap-1">
          <FilePick onFile={photo} testid={`admin-item-photo-${it.id}`} className="icon-btn !h-9 !w-9"><ImagePlus size={15} /></FilePick>
          <button type="button" onClick={() => onEdit(it)} className="icon-btn !h-9 !w-9" aria-label="Editar" data-testid={`admin-item-edit-${it.id}`}><Pencil size={15} /></button>
          <button type="button" onClick={() => onDelete(it)} className="icon-btn !h-9 !w-9" aria-label="Eliminar" data-testid={`admin-item-delete-${it.id}`}><Trash2 size={15} /></button>
        </div>
      </td>
    </tr>
  );
};

const BrandLogos = ({ names }) => {
  const [logos, setLogos] = useState({});
  useEffect(() => { getJson("/catalog/brands").then((d) => setLogos(Object.fromEntries((Array.isArray(d) ? d : []).map((b) => [b.name, b.logo_url])))); }, []);
  const send = async (name, f) => {
    try { const d = await upload("/admin/catalog/brands/logo", f, { name }); setLogos((s) => ({ ...s, [name]: d.logo_url })); toast.success(`Logo de ${name} actualizado`); } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="mt-10" data-testid="admin-brand-logos">
      <p className="font-display text-2xl">Logos de marcas</p>
      <p className="mt-1 text-sm text-[var(--muted)]">Sin logo, la marca se muestra como texto en la ficha.</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {names.map((n) => (
          <FilePick key={n} onFile={(f) => send(n, f)} testid={`admin-brand-${n.replace(/\W+/g, "-").toLowerCase()}`} className="flex items-center gap-3 rounded-xl border border-white/10 p-3 transition-colors hover:border-white/30">
            <span className="grid h-10 w-16 place-items-center rounded bg-white/5">{logos[n] ? <img src={mediaUrl(logos[n])} alt={n} className="max-h-8 max-w-14 object-contain" /> : <ImagePlus size={15} className="text-[var(--muted)]" />}</span>
            <span className="text-sm">{n}</span>
          </FilePick>
        ))}
      </div>
    </div>
  );
};

export const AdminCatalog = () => {
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const load = useCallback(() => { getJson("/catalog/items").then((d) => setItems(Array.isArray(d) ? d : [])); }, []);
  useEffect(() => { load(); }, [load]);
  const replace = (it) => setItems((s) => s.map((x) => (x.id === it.id ? it : x)));
  const remove = async (it) => {
    if (!window.confirm(`¿Eliminar "${it.name}" del catálogo?`)) return;
    const res = await call(`/admin/catalog/items/${it.id}`, { method: "DELETE" });
    if (res.ok) setItems((s) => s.filter((x) => x.id !== it.id));
  };
  const shown = useMemo(() => (items ?? []).filter((it) => (!cat || it.category === cat) && (!q || `${it.name} ${it.manufacturer}`.toLowerCase().includes(q.toLowerCase()))), [items, q, cat]);
  const brands = useMemo(() => [...new Set((items ?? []).map((i) => i.manufacturer))].sort(), [items]);
  return (
    <div data-testid="admin-catalog">
      <AiExpand onDone={load} />
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar pieza o marca" className="support-input max-w-xs" data-testid="admin-catalog-search" />
        <select value={cat} onChange={(e) => setCat(e.target.value)} className="support-input max-w-[220px]" data-testid="admin-catalog-category">
          <option value="">Todas las categorías</option>
          {CATALOG_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <span className="mono text-[11px] text-[var(--muted)]" data-testid="admin-catalog-count">{shown.length} piezas</span>
        <button type="button" onClick={() => setEdit({})} className="btn-primary ml-auto" data-testid="admin-add-item-btn"><Plus size={16} /> Nueva pieza</button>
      </div>
      <div className="mt-5 overflow-x-auto rounded-2xl border border-white/10" data-lenis-prevent>
        <table className="w-full min-w-[820px] text-left text-sm">
          <thead className="mono bg-white/5 text-[11px] uppercase tracking-[0.15em] text-[var(--muted)]">
            <tr>{["", "Pieza", "Marca · año", "Categoría", "En la guarida", ""].map((h, i) => <th key={i} className="px-3 py-3 font-normal">{h}</th>)}</tr>
          </thead>
          <tbody>
            {shown.map((it) => <Row key={it.id} it={it} onEdit={setEdit} onChange={replace} onDelete={remove} />)}
          </tbody>
        </table>
        {items === null && <Loader2 className="mx-auto my-10 animate-spin" />}
      </div>
      <BrandLogos names={brands} />
      <ItemForm key={edit?.id ?? "new"} item={edit} onClose={() => setEdit(null)} onSaved={(it) => { setEdit(null); load(); toast.success(`"${it.name}" guardada`); }} />
    </div>
  );
};
